import { QuestionPromptCard, type QuestionPictureInfo } from "@/components/QuestionPrompt";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { OptionLabel } from "@/components/OptionLabel";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";

interface MCQProps {
  promptMd: string;
  /** EP-19: the picture under the prompt, if the question has one. */
  picture?: QuestionPictureInfo | null;
  // Server pre-shuffles (per attempt + position) so the candidate JSON
  // never contains a correct-answer signal. Locked down 2026-05-27
  // (pre-ship-audit QA-001).
  options: string[];
  selected: string | null;
  onSelect: (value: string) => void;
}

// Kept exported for Practice.tsx — server-side shuffling owns the candidate
// flow now, but the static practice questions still shuffle client-side.
export function shuffleStable<T>(list: T[], seed: number): T[] {
  const arr = [...list];
  let s = seed;
  for (let i = arr.length - 1; i > 0; i--) {
    s = (s * 9301 + 49297) % 233280;
    const j = Math.floor((s / 233280) * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export function MCQ({
  promptMd,
  picture,
  options,
  selected,
  onSelect,
}: MCQProps) {
  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <QuestionPromptCard promptMd={promptMd} picture={picture} />

      <Card>
        <CardHeader>
          <CardTitle>Choose one answer</CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={selected ?? ""}
            onValueChange={onSelect}
            className="grid gap-3"
          >
            {options.map((opt, idx) => {
              const id = `mcq-opt-${idx}`;
              const isSelected = selected === opt;
              return (
                <Label
                  key={id}
                  htmlFor={id}
                  className={cn(
                    "flex cursor-pointer items-start gap-3 rounded-md border p-4 transition-colors",
                    "hover:bg-accent",
                    isSelected
                      ? "border-primary bg-primary/5"
                      : "border-border"
                  )}
                >
                  <RadioGroupItem id={id} value={opt} className="mt-0.5" />
                  <span className="text-base leading-snug">
                    <OptionLabel text={opt} />
                  </span>
                </Label>
              );
            })}
          </RadioGroup>
        </CardContent>
      </Card>
    </div>
  );
}

export default MCQ;
